// src/components/ReviewsCarousel.js
// Site-wide widget showing a shuffled sample of reviews across all products.
// Drop it into any page (footer, homepage, etc).

import React, { useEffect, useState, useRef } from 'react';
import { getReviews, getStoreRating } from '../lib/reviewsApi';
import StarRating from './StarRating';

const VISIBLE_COUNT = 3;
const ROTATE_INTERVAL_MS = 5000;
const CACHE_TTL_MS = 5 * 60 * 1000;
const REVIEW_PREVIEW_LENGTH = 140;

// Module-level cache so navigating between pages doesn't re-fetch every mount.
let cache = null;

async function getCachedReviews() {
    if (cache && Date.now() - cache.fetchedAt < CACHE_TTL_MS) {
        return cache.data;
    }
    const data = await getReviews({ limit: 8 });
    cache = { data, fetchedAt: Date.now() };
    return data;
}

const ReviewsCarousel = () => {
    const [reviews, setReviews] = useState([]);
    const [storeRating, setStoreRating] = useState(null);
    const [expandedReviews, setExpandedReviews] = useState({});
    const [startIndex, setStartIndex] = useState(0);
    const timerRef = useRef(null);
    const safeReviews = Array.isArray(reviews) ? reviews : [];

    useEffect(() => {
        getCachedReviews()
            .then(setReviews)
            .catch((err) => console.error('Failed to load reviews:', err));
        getStoreRating().then(setStoreRating);
    }, []);

    useEffect(() => {
        if (safeReviews.length <= VISIBLE_COUNT) return;
        timerRef.current = setInterval(() => {
            setStartIndex((i) => (i + 1) % safeReviews.length);
        }, ROTATE_INTERVAL_MS);
        return () => clearInterval(timerRef.current);
    }, [safeReviews.length]);

    if (safeReviews.length === 0) return null;

    const visible = Array.from({ length: Math.min(VISIBLE_COUNT, safeReviews.length) }, (_, i) => safeReviews[(startIndex + i) % safeReviews.length]);

    return (
        <section className="py-24 px-6 bg-muted/30 transition-colors duration-300">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <p className="text-xs tracking-[0.3em] uppercase text-[#CEAC8F] mb-3">What People Are Saying</p>
                    <h2 className="text-4xl md:text-5xl font-light text-foreground">Loved, Long After</h2>
                    {storeRating && storeRating.count > 0 && (
                        <div className="mt-5 flex flex-col items-center gap-2" aria-label={`Rated ${storeRating.average.toFixed(1)} out of 5 from ${storeRating.count} reviews on Judge.me`}>
                            <StarRating rating={Math.round(storeRating.average)} size={20} />
                            <p className="text-sm text-muted-foreground">
                                <span className="font-medium text-foreground">{storeRating.average.toFixed(1)} out of 5</span>
                                {' · '}{storeRating.count} review{storeRating.count !== 1 ? 's' : ''} on Judge.me
                            </p>
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {visible.map((review, i) => {
                        const reviewKey = `${review.created_at || 'review'}-${review.customer_name || 'anonymous'}-${i}`;
                        const isLong = review.body.length > REVIEW_PREVIEW_LENGTH;
                        const isExpanded = Boolean(expandedReviews[reviewKey]);
                        const reviewBody = isLong && !isExpanded
                            ? `${review.body.slice(0, REVIEW_PREVIEW_LENGTH).trimEnd()}…`
                            : review.body;

                        return (
                        <div key={reviewKey} className="bg-card rounded-2xl border border-border p-6 flex flex-col">
                            <StarRating rating={review.rating} size={14} />
                            <div className="my-4 flex-1">
                                <p id={`review-${i}`} className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">
                                    “{reviewBody}”
                                </p>
                                {isLong && (
                                    <button
                                        type="button"
                                        onClick={() => setExpandedReviews((current) => ({
                                            ...current,
                                            [reviewKey]: !current[reviewKey],
                                        }))}
                                        aria-expanded={isExpanded}
                                        aria-controls={`review-${i}`}
                                        className="mt-2 text-sm font-medium text-[#CEAC8F] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#CEAC8F]"
                                    >
                                        {isExpanded ? 'Show less' : 'Show more'}
                                    </button>
                                )}
                            </div>
                            <p className="text-foreground text-sm font-medium">{review.customer_name || 'Anonymous'}</p>
                            <p className="text-[#CEAC8F]/70 text-xs tracking-widest uppercase mt-1">{review.product_title}</p>
                        </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default ReviewsCarousel;
